import React from "react";
import SEO from "../components/SEO";

export default function Terms() {
  return (
    <section className="page container">
      <SEO
        title="BossDrop | Terms of Service"
        description="The terms that govern use of BossDrop, including connected Third-Party Services, AI outputs, approvals, and your responsibilities as a customer."
        canonical="https://bossdrop.ai/terms"
        url="https://bossdrop.ai/terms"
        /* image="https://bossdrop.ai/og/pricing-1200x630.jpg" */ 
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Pricing",
              item: "https://bossdrop.ai/terms",
            },
          ],
        }}
      />
      <div className="hero">
        <h1 className="h1">Terms of Service</h1>
        <p className="sub">Last updated: August 18, 2025</p>
      </div>
      <div className="panel">
        <h2 className="h2">1) Agreement</h2>
        <p>
          These Terms of Service (“Terms”) govern your access to and use of the
          BossDrop platform, websites, mobile/desktop apps, APIs, and related
          services (collectively, the “Service”). By creating an account,
          connecting integrations, or using the Service, you agree to these
          Terms and our Privacy Policy.
        </p>
        <p className="muted">
          If you are accepting on behalf of a company, you represent and warrant
          that you have authority to bind that company. “You” and “Customer”
          mean the organization or person using the Service.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">2) The Service (high level)</h2>
        <p>
          BossDrop provides AI-driven work management and automation features
          that plan, assign, summarize, and escalate work across tools like
          Slack, Microsoft Teams, Microsoft Outlook/365, Gmail, Google
          Calendar, Asana, Jira, Linear, ClickUp, Notion, monday.com, Microsoft
          Planner, GitHub, HubSpot, and other systems you connect
          (“Third-Party Services”). Outputs may
          include schedules, summaries, recommendations, task assignments, or
          automated messages sent via those connected tools.
        </p>
        <ul className="bullets">
          <li>
            You control which integrations are enabled and the access scopes.
          </li>
          <li>
            You remain responsible for your work processes, compliance, and any
            employment or managerial decisions. AI outputs may be inaccurate or
            incomplete and must be reviewed by humans where appropriate.
          </li>
          <li>
            BossDrop is not a law, HR, accounting, medical, or financial
            advisor.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">3) Accounts & Organization Admins</h2>
        <ul className="bullets">
          <li>
            You must provide accurate registration information and keep it
            updated. You are responsible for credentials and all activities
            under your account.
          </li>
          <li>
            Organization administrators can invite/remove users, configure
            integrations and data retention, and view certain audit logs.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">4) Subscriptions, Fees, Taxes</h2>
        <ul className="bullets">
          <li>
            Access may be provided via free trials or paid plans. Paid plans are
            billed in advance (monthly or annually) and are non-refundable
            except where required by law.
          </li>
          <li>
            Prices, features, and limits may change on renewal or with notice
            for month-to-month plans. We may offer usage-based add-ons (e.g.,
            message volume, automation runs, API calls).
          </li>
          <li>You are responsible for all applicable taxes.</li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">5) Acceptable Use</h2>
        <ul className="bullets">
          <li>
            Do not use the Service to violate law, intellectual-property,
            privacy, or employment/monitoring rules, or to send spam or abusive
            messages.
          </li>
          <li>
            Do not attempt to reverse engineer, scrape, overload, or circumvent
            technical limits or security.
          </li>
          <li>
            Do not input or store highly sensitive data unless you have a
            written agreement permitting it (e.g., PCI card data, PHI/medical
            records, government-classified data, passwords/secrets).
          </li>
          <li>
            You must have lawful permission to process any personal data of your
            workforce, contractors, or customers inside BossDrop.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">6) Third-Party Services</h2>
        <p>
          The Service connects to Third-Party Services you choose. Their terms
          govern your use of those services. We are not responsible for
          Third-Party Services or changes they make to APIs or permissions. You
          can disconnect an integration at any time; doing so may degrade or
          disable features that rely on it.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">7) Customer Data & AI Outputs</h2>
        <p>
          “Customer Data” means content and data you provide or authorize us to
          pull from connected tools (calendar events, tasks, messages, files,
          metadata, and configuration). You own Customer Data. Subject to these
          Terms, you grant BossDrop a worldwide, non-exclusive license to host,
          process, and display Customer Data solely to provide and improve the
          Service, including safety, reliability, analytics, and feature
          development.
        </p>
        <ul className="bullets">
          <li>
            Unless you expressly opt in, Customer Data is not used to train
            foundation models for third parties. We may use de-identified or
            aggregated data to improve the Service.
          </li>
          <li>
            AI outputs are generated probabilistically and may be incorrect;
            you’re responsible for review and for any actions taken in your
            environments.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">8) Security & Privacy</h2>
        <p>
          We implement reasonable technical and organizational safeguards. No
          system is perfectly secure. See our{" "}
          <a href="/privacy">Privacy Policy</a> for details. If we believe an
          unauthorized disclosure affecting you occurred, we’ll notify you
          consistent with law and our incident practices.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">9) Confidentiality</h2>
        <p>
          Each party may access the other’s confidential information. The
          receiving party will use it only to perform under these Terms and
          protect it with at least reasonable care. This does not cover
          information that is public, independently developed, or rightfully
          obtained from a third party without duty of confidentiality.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">10) IP; Feedback</h2>
        <p>
          BossDrop and its licensors own all rights in the Service. No rights
          are granted except as expressly set out here. You grant BossDrop a
          royalty-free, perpetual license to use and incorporate any suggestions
          or feedback you provide without obligation.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">11) Beta/Pre-release Features</h2>
        <p>
          We may offer experimental features marked alpha/beta/preview. They are
          provided “as is,” may be disabled at any time, and may have different
          security or support commitments.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">12) Suspension & Termination</h2>
        <ul className="bullets">
          <li>
            We may suspend or terminate for material breach, legal risk,
            security risk, non-payment, or abusive behavior.
          </li>
          <li>
            You may terminate at any time; fees already paid are non-refundable
            unless required by law.
          </li>
          <li>
            Upon termination, we will delete or return Customer Data per your
            admin settings and our data-retention windows (see Privacy Policy).
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">13) Disclaimers</h2>
        <p>
          THE SERVICE AND ALL OUTPUTS ARE PROVIDED “AS IS.” TO THE MAXIMUM
          EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR
          IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
          AND NON-INFRINGEMENT. WE DO NOT WARRANT UNINTERRUPTED OR ERROR-FREE
          OPERATION, OR THAT OUTPUTS WILL MEET YOUR REQUIREMENTS.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">14) Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE
          FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
          PUNITIVE DAMAGES, OR LOSS OF PROFITS, REVENUE, GOODWILL, OR DATA.
          EXCEPT FOR PAYMENT OBLIGATIONS, DATA/SECURITY BREACHES CAUSED BY GROSS
          NEGLIGENCE OR WILLFUL MISCONDUCT, OR INDEMNITY OBLIGATIONS, A PARTY’S
          AGGREGATE LIABILITY UNDER THESE TERMS WILL NOT EXCEED THE AMOUNTS PAID
          OR PAYABLE BY YOU TO BOSSDROP FOR THE SERVICE IN THE TWELVE (12)
          MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">15) Indemnity</h2>
        <ul className="bullets">
          <li>
            We will defend and indemnify you against third-party claims that the
            Service (as provided by us) infringes IP rights, subject to standard
            exclusions (e.g., combinations not supplied by us, your content, or
            use in violation of these Terms).
          </li>
          <li>
            You will defend and indemnify us against claims arising from your
            Customer Data, your use of the Service, or your violation of law.
          </li>
        </ul>
      </div>
      <div className="panel">
        <h2 className="h2">16) Data Processing</h2>
        <p>
          For personal data in Customer Data, BossDrop acts as a processor to
          you (the controller). Our <a href="/privacy">Privacy Policy</a> and
          any signed DPA (if applicable) govern such processing and incorporate
          appropriate cross-border transfer mechanisms.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">17) Export & Sanctions</h2>
        <p>
          You must comply with export, sanctions, and embargo laws. You will not
          use the Service in prohibited regions or for prohibited end uses.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">18) Governing Law; Venue</h2>
        <p>
          These Terms are governed by the laws of the State of Delaware, without
          regard to conflicts of law. Exclusive jurisdiction and venue lie in
          the state and federal courts located in New Castle County, Delaware,
          and each party consents to personal jurisdiction there.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">19) Changes</h2>
        <p>
          We may update these Terms from time to time. Material changes will be
          posted on the website or communicated to admins. Your continued use
          after the effective date constitutes acceptance.
        </p>
      </div>
      <div className="panel">
        <h2 className="h2">20) Contact</h2>
        <p>
          Questions? Email{" "}
          <a href="mailto:info@bossdrop.ai">info@bossdrop.ai</a>.
        </p>
      </div>
    </section>
  );
}
